import { DownloadOutlined } from '@ant-design/icons';
import { Col, Form, Row } from 'antd';
import dayjs from 'dayjs';
import { endsWith } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import fileDownload from 'services/fileDownload';
import emiseApi, { ISezemiseKorpAkce } from 'services/korporatniAkce/emise';
import {
    IPruvodceVYPIS,
    pruvodceVYPISGETApi,
    pruvodceVYPISPOSTApi,
    pruvodceVYPISPUTApi,
    pruvodceVYPIScertApi
} from 'services/korporatniAkce/pruvodceVYPIS';
import { zhrnutiVYPISdownload } from 'services/korporatniAkce/zhrnutiDownload';

import { ENotificationType } from 'enums/notificationType';
import routePathEnum from 'enums/routePathEnum';

import bicValidation from 'utils/validations/bicValidation';
import daysAfterValidation from 'utils/validations/daysAfterValidation';
import emailValidation from 'utils/validations/emailValidation';
import leiValidation from 'utils/validations/leiValidation';
import pscValidation from 'utils/validations/pscValidation';

let utc = require('dayjs/plugin/utc');
let timezone = require('dayjs/plugin/timezone');

const tz = 'Europe/Prague';

dayjs.extend(utc);
dayjs.extend(timezone);

export const PruvodceVYPIS: React.FC = () => {
    const [form] = Form.useForm();
    const { navigateWorkaround } = useNavigateWorkaround();
    const { idSRD, isinSRD, aktivniFirma, prevPath, NotificationComponent, cisStaty, profily } = useContext(Context);
    const { setLevel } = useProfily();
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingPXklic, setLoadingPXklic] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    const [downloading, setDownloading] = useState<boolean>(false);
    const [data, setData] = useState<IPruvodceVYPIS>();
    const [, setEmise] = useState<ISezemiseKorpAkce[]>([]);

    useEffect(() => {
        setLoading(true);
        emiseApi()
            .then(resp => {
                setEmise(resp.data.sezemise);
                form.setFieldsValue({
                    ...data,
                    lei: resp.data.sezemise.find(({ isin }) => isin === isinSRD)?.lei
                });
                return resp;
            })
            .then(resp => {
                if (idSRD && endsWith(prevPath, routePathEnum.KORPORATNI_AKCE)) {
                    pruvodceVYPISGETApi(idSRD)
                        .then(rezp => {
                            setData({
                                ...rezp.data,
                                isin: isinSRD || ''
                            });
                            form.setFieldsValue(data);
                        })
                        .finally(() => setLoading(false));
                } else {
                    setData(val => ({
                        ...val,
                        isin: isinSRD || '',
                        lei: resp.data.sezemise.find(({ isin }) => isin === isinSRD)?.lei
                    }));
                    setLoading(false);
                }
            })
            .catch(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, idSRD, isinSRD, prevPath]);

    useEffect(() => {
        return () => {
            setLevel('idSRD', null);
            setLevel('prevPath', '');
        };
    }, [setLevel]);

    // nastavenie casovej zony pre tuto page
    useEffect(() => {
        dayjs.tz.setDefault('Europe/Prague');
        return () => {
            dayjs.tz.setDefault();
        };
    }, []);

    const downloadZhrnuti = () => {
        form.validateFields([
            'ISIN',
            'LEI_CELE',
            'SRD_PRUVODCE_VH_B_ID',
            'SRD_DLAZDICE_ROZHODNY_DEN',
            'SRD_PRUVODCE_VYPIS_MEZNIDATUM_CET'
        ]).then(() => {
            idSRD && setDownloading(true);
            idSRD &&
                data &&
                zhrnutiVYPISdownload(idSRD, data)
                    .then(resp => fileDownload(resp.data.redirect))
                    .finally(() => setDownloading(false));
        });
    };

    const sendVYPIS = () => {
        if (!idSRD || !data) return;

        if (page === 1) {
            setLoading(true);
            pruvodceVYPISPUTApi(idSRD, data)
                .then(() => {
                    page === 1 && setPage(pv => pv + 1);
                })
                .finally(() => setLoading(false));
        } else if (page === 2) {
            profily?.typprihlaseni !== 'PXKLIC' ? setLoading(true) : setLoadingPXklic(true);
            (profily?.typprihlaseni === 'CERT' ? pruvodceVYPIScertApi(idSRD, data) : pruvodceVYPISPOSTApi(idSRD, data))
                .then(resp => {
                    if ((resp.data.upozor || []).length) {
                        resp.data.upozor.map(e =>
                            NotificationComponent({
                                title: e.typ
                            })
                        );
                    } else {
                        NotificationComponent({
                            description: 'ZADOST_ODESLANA',
                            title: 'SRD_CIS_UDALOST_VYPIS'
                        });
                        navigateWorkaround(
                            `${routePathEnum.FIRMA_PLAIN}${aktivniFirma?.iducet}${routePathEnum.KORPORATNI_AKCE}`
                        );
                    }
                })
                .catch((err: any) =>
                    NotificationComponent({
                        title: err.response?.data.code,
                        description: err.response?.data.message,
                        type: ENotificationType.ERROR
                    })
                )
                .finally(() => (profily?.typprihlaseni !== 'PXKLIC' ? setLoading(false) : setLoadingPXklic(false)));
        }
    };

    return (
        <LoadingWrapper
            loading={loading || loadingPXklic}
            loadingText={profily?.typprihlaseni === 'PXKLIC' && loadingPXklic ? 'LOADING_PXKLIC' : 'NACITANI'}
        >
            <FormWrapper onFinish={() => sendVYPIS()} outForm={form}>
                <Title level={3} text="SRD_NOVA_OZNAMENI_ZADOST" />
                {data && (
                    <>
                        <Title level={5} dividerTitle={true} text="SRD_PRUVODCE_VH_A" />
                        <Row>
                            <Col span={24}>
                                <InputComponent
                                    disabled={page === 2}
                                    name="isin"
                                    label="ISIN"
                                    capitalize={true}
                                    initialValue={isinSRD || undefined}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            isin: value.target.value
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                />
                            </Col>
                        </Row>
                        <Title level={5} dividerTitle={true} text="UDAJE_O_EMITENTOVI" />
                        <Row>
                            <Col span={24}>
                                <InputComponent
                                    disabled={page === 2}
                                    name="lei"
                                    label="LEI_CELE"
                                    initialValue={data?.lei}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            lei: value.target.value
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                />
                            </Col>
                        </Row>
                        <Title level={5} dividerTitle={true} text="SRD_PRUVODCE_VH_B" />
                        <Row>
                            <Col span={24}>
                                <InputComponent
                                    disabled={page === 2}
                                    name="mtgid"
                                    label="SRD_PRUVODCE_VH_B_ID"
                                    maxLength={35}
                                    initialValue={data?.mtgid ? data.mtgid : `${isinSRD}-`}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            mtgid: value.target.value
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                    rules={[
                                        () => ({
                                            validator(_rule, value: string) {
                                                if (!value || (value.includes('-') && value.split('-')[1].length > 0))
                                                    return Promise.resolve();
                                                return Promise.reject(
                                                    <TranslateText>NEVALIDNI_IDENTIFIKATOR</TranslateText>
                                                );
                                            }
                                        })
                                    ]}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <DatePickerComponent
                                    disabled={page === 2}
                                    name="rozhodnyden"
                                    label="SRD_DLAZDICE_ROZHODNY_DEN"
                                    initialValue={data?.rozhodnyden ? dayjs(data.rozhodnyden).tz(tz) : undefined}
                                    format="DD.MM.YYYY"
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            rozhodnyden: value?.utc().tz(tz).format('YYYY-MM-DD')
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <DatePickerComponent
                                    disabled={page === 2}
                                    name="meznidatum"
                                    label="SRD_PRUVODCE_VYPIS_MEZNIDATUM_CET"
                                    initialValue={data?.meznidatum ? dayjs(data.meznidatum).tz(tz) : undefined}
                                    format="DD.MM.YYYY"
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            meznidatum: value?.utc().tz(tz).format('YYYY-MM-DD')
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                    rules={daysAfterValidation(3, data?.rozhodnyden)}
                                />
                            </Col>
                        </Row>
                        <Title level={5} dividerTitle={true} text="SRD_PRUVODCE_VYPIS_C" />

                        <Row>
                            <Col span={24}>
                                <InputComponent
                                    name="leiprijemce"
                                    label="LEI_CELE"
                                    initialValue={data.leiprijemce}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            leiprijemce: value.target.value
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                    disabled={page === 2}
                                    rules={leiValidation()}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <InputComponent
                                    name="bic"
                                    label="BIC"
                                    initialValue={data.bic}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            bic: value.target.value
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                    disabled={page === 2}
                                    rules={bicValidation()}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <InputComponent
                                    name="jmeno"
                                    label="JMENO"
                                    initialValue={data.jmeno}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            jmeno: value.target.value
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                    disabled={page === 2}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <InputComponent
                                    name="ulice"
                                    label="ULICE_CP"
                                    initialValue={data.ulice}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            ulice: value.target.value
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                    disabled={page === 2}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <InputComponent
                                    name="mesto"
                                    label="MESTO"
                                    initialValue={data.mesto}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            mesto: value.target.value
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                    disabled={page === 2}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <InputComponent
                                    name="psc"
                                    label="PSC"
                                    initialValue={data.psc}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            psc: value.target.value
                                        }))
                                    }
                                    rules={pscValidation('hqlangcountry')}
                                    maxLength={10}
                                    labelCol={{
                                        span: 8
                                    }}
                                    disabled={page === 2}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <SelectComponent
                                    name="stat"
                                    disabled={page === 2}
                                    labelParagraph="STAT"
                                    isFormItem={true}
                                    data={cisStaty}
                                    defaultValue={data?.stat}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            stat: value
                                        }))
                                    }
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <InputComponent
                                    name="email"
                                    label="EMAIL"
                                    initialValue={data.email}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            email: value.target.value
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                    disabled={page === 2}
                                    rules={emailValidation()}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <InputComponent
                                    name="url"
                                    label="URL_ADRESA"
                                    initialValue={data.url}
                                    onChange={value =>
                                        setData(val => ({
                                            ...val,
                                            url: value.target.value
                                        }))
                                    }
                                    labelCol={{
                                        span: 8
                                    }}
                                    disabled={page === 2}
                                />
                            </Col>
                        </Row>
                    </>
                )}
                {page === 2 && (
                    <Row className="button-row">
                        <Col>
                            <ButtonComponent
                                id="stahnoutSrdBtn"
                                icon={<DownloadOutlined />}
                                buttontext="SRD_STAHNOUT_XML"
                                loading={downloading}
                                type="default"
                                onClick={() => downloadZhrnuti()}
                            />
                        </Col>
                    </Row>
                )}
                <Row>
                    {page !== 1 && (
                        <Col>
                            <ButtonComponent
                                id="zpetBtn"
                                buttontext="ZPET"
                                onClick={() => setPage(pv => pv - 1)}
                                type="default"
                            />
                        </Col>
                    )}
                    <Col>
                        <ButtonComponent
                            id="submitBtn"
                            buttontext={page === 1 ? 'POKRACOVAT' : 'ODESLAT'}
                            onClick={() => form.submit()}
                            loading={loading}
                        />
                    </Col>
                </Row>
            </FormWrapper>
        </LoadingWrapper>
    );
};
